/**
 * @fileoverview Serviço responsável por configurar a instância HTTP principal, autenticação e refresh de token.
 */

import axios from 'axios';
import { isPublicPage } from "../utils/publicPages";

export const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://talkapi.sttelar.pro';

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Incluir cookies por padrão
});

// 403 de falta de permissão (os demais 403 são regra de negócio, como a janela
// de 24 h da Meta).
const PERMISSION_ERRORS = ["ERR_NO_PERMISSION", "ERR_NO_ATTENDANCE_ACCESS"];

// Flag para prevenir loop infinito
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Bloquear as requisições com login nas telas públicas (utils/publicPages)
    const isPublicRoute = isPublicPage();

    // Permitir apenas requisições de autenticação em rotas públicas
    const isAuthRequest = config.url?.includes('/auth/login') ||
                          config.url?.includes('/auth/signup') ||
                          config.url?.includes('/auth/refresh_token') ||
                          config.url?.includes('/auth/mfa/');

    if (isPublicRoute && !isAuthRequest) {
      return Promise.reject(new Error('Requisição bloqueada em rota pública'));
    }

    const token = localStorage.getItem('token');
    if (token && !config._retry) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // NÃO interceptar erros de login e logout - deixar os componentes processarem
    if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/logout')) {
      return Promise.reject(error);
    }

    // Prevenir loop infinito — apenas 401 dispara refresh de token. 403 é
    // "permissão negada" semântico (ex.: janela 24h Meta, AppError de regra
    // de negócio) e NÃO deve ser retentado, senão a requisição é executada
    // 2x no backend (criando 2 mensagens locais, 2 toasts, etc.).
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Se já estamos fazendo refresh, enfileirar a requisição
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const token = localStorage.getItem('token');

      if (token) {
        try {
          // Fazer requisição de refresh SEM interceptor, mas COM cookies
          const response = await axios.post(`${API_BASE_URL}/auth/refresh_token`, {}, {
            withCredentials: true // Incluir cookies para o refresh token
          });

          const { token: newToken } = response.data;
          localStorage.setItem('token', newToken);
          
          // Processar fila de requisições pendentes
          processQueue(null, newToken);
          
          // Tentar novamente a requisição original
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Limpar token e redirecionar para login
          localStorage.removeItem('token');
          localStorage.removeItem('companyId');
          localStorage.removeItem('userId');
          
          // Redirecionar para login
          window.location.href = '/login';
          
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Sem token - apenas limpar e rejeitar
        localStorage.removeItem('token');
        localStorage.removeItem('companyId');
        localStorage.removeItem('userId');
        return Promise.reject(error);
      }
    }

    // Permissão negada: o perfil pode ter mudado desde o login (o servidor
    // confere o de agora). O useAuth relê a sessão; a requisição não é repetida.
    if (error.response?.status === 403 && PERMISSION_ERRORS.includes(error.response.data?.error)) {
      window.dispatchEvent(new Event("auth:forbidden"));
    }

    // Acesso restrito (etapa 9 dos planos): a Empresa passou do plano sem a escolha do que fica. A cobrança
    // é relida e a tela troca o conteúdo pelo aviso (o admin vai para Faturamento). A suspensão pelo atraso
    // (etapa 10): a cobrança é relida e a faixa aparece já na primeira recusa.
    const billingRefusals = ["ERR_PLAN_ADEQUACY_REQUIRED", "ERR_SENDING_SUSPENDED", "ERR_INTERNAL_CHAT_SUSPENDED"];
    if (error.response?.status === 403 && billingRefusals.includes(error.response.data?.error)) {
      window.dispatchEvent(new Event("billing:refresh"));
    }

    return Promise.reject(error);
  }
);

export const openApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true // Incluir cookies por padrão
});

export default api;
